import type { Dispatch, SetStateAction } from 'react';
import { applyActivityOrder, applyDaySchedule } from '../../utils/activityOrder';
import type { AiPersistedAction } from '../../api/aiProposals';
import type { NextDayUpdate } from '../../api/itinerary.api';
import type { ItineraryDay } from '../../types';

// Applies AI itinerary proposals to the page-level `days` state. Extracted from
// ItineraryTab so the central dispatcher (useAiActionDispatch) can apply these
// from ANY tab — itinerary data lives in TripContext, not in ItineraryTab's
// local state, so this works whether or not the itinerary tab is mounted.

function applyNextDayUpdate(dayList: ItineraryDay[], ndu: NextDayUpdate): ItineraryDay[] {
  return dayList.map(d => {
    if (d.id !== ndu.dayId) return d;
    return {
      ...d,
      startFromPrevStop: true,
      activities: [ndu.originActivity, ...d.activities.filter(a => !a.isOrigin)],
    };
  }) as ItineraryDay[];
}

export function applyItineraryAiActions(
  setDays: Dispatch<SetStateAction<ItineraryDay[]>>,
  actions: AiPersistedAction[],
): void {
  for (const action of actions) {
    if (action.type === 'activity_created') {
      setDays(prev => {
        let updated = prev.map(d =>
          d.id === action.dayId ? { ...d, activities: [...d.activities, action.data] } : d,
        );
        if (action.nextDayUpdate) updated = applyNextDayUpdate(updated, action.nextDayUpdate);
        return updated;
      });
    } else if (action.type === 'activity_moved') {
      const act = action.data;
      // Drop the card that left by its ORIGINAL id: a booking card is rebuilt on
      // the target day, so the row that comes back has a different one.
      setDays(prev => prev
        .map(d => ({ ...d, activities: d.activities.filter(a => a.id !== action.fromActivityId && a.id !== act.id) }))
        .map(d => d.id === act.dayId ? { ...d, activities: [...d.activities, act] } : d));
    } else if (action.type === 'activity_updated') {
      // Same as a manual edit from the activity form: the saved row replaces the old one.
      setDays(prev => prev.map(d => d.id === action.dayId
        ? { ...d, activities: d.activities.map(a => (a.id === action.data.id ? action.data : a)) }
        : d));
    } else if (action.type === 'activity_deleted') {
      setDays(prev => {
        const origDay = prev.find(d => d.id === action.dayId);
        let updated = prev.map(d => ({
          ...d,
          activities: d.activities.filter(a => a.id !== action.activityId),
        }));
        // Mirror backend cascade: if the deleted activity was the last non-origin
        // of its day, clear the next day's isOrigin marker.
        if (origDay) {
          const nonOriginBefore = origDay.activities.filter(a => !a.isOrigin);
          const lastNonOrigin = nonOriginBefore.sort((a, b) => b.sortOrder - a.sortOrder)[0];
          if (lastNonOrigin?.id === action.activityId) {
            const nextDay = updated.find(d => d.dayNumber === origDay.dayNumber + 1);
            if (nextDay?.startFromPrevStop) {
              updated = updated.map(d =>
                d.id === nextDay.id
                  ? { ...d, activities: d.activities.filter(a => !a.isOrigin), startFromPrevStop: false }
                  : d,
              );
            }
          }
        }
        return updated;
      });
    } else if (action.type === 'day_plan_applied') {
      setDays(prev => {
        let updated = prev.map(d =>
          d.id === action.dayId
            ? { ...d, activities: [...d.activities, ...action.activities] }
            : d,
        );
        if (action.nextDayUpdate) updated = applyNextDayUpdate(updated, action.nextDayUpdate);
        return updated;
      });
    } else if (action.type === 'activities_reordered') {
      setDays(prev => {
        let updated = prev.map(d =>
          d.id === action.dayId
            ? { ...d, activities: applyDaySchedule(d.startTime, applyActivityOrder(d.activities, action.activityIds)) }
            : d,
        );
        if (action.nextDayUpdate) updated = applyNextDayUpdate(updated, action.nextDayUpdate);
        return updated;
      });
    }
  }
}
